import { Box, CircularProgress, LinearProgress, Typography } from "@mui/material"
import { ArrowDownwardOutlined, ArrowUpwardOutlined, ChevronRightOutlined } from "@mui-symbols-material/w300"
import { useSearch } from "@tanstack/react-router"
import { flexRender } from "@tanstack/react-table"
import React, { Suspense, useEffect, useRef } from "react"
import { useShallow } from "zustand/shallow"

import { useSupplierReportSearchStore } from "../../stores/useSupplierReportSearchStore"

import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { PurchaseOrderDialog } from "@/screens/Procurement/PurchaseOrder/components/dialogs/PurchaseOrderDialog"
import { hasValidShipFromLocations } from "@/screens/Procurement/PurchaseOrder/utils/common"
import { SupplierReportProductTableRow } from "@/screens/Procurement/Supplier/components/Table/SupplierReportProductTableRow"
import { useSupplierReportPurchaseOrder } from "@/screens/Procurement/Supplier/hooks/useSupplierReportPurchaseOrder"
import { useSupplierReportTable } from "@/screens/Procurement/Supplier/hooks/useSupplierReportTable"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

const LazyExpandedContent = React.lazy(() => import("./ExpandedRow/SupplierReportProductTable"))

export const SupplierReportTable: React.FC = () => {
  const currentSearch = useSearch({ from: "/app/procurement/_layout/supplier-report" })

  const data = useSupplierReportSearchStore(({ data }) => data)
  const fetching = useSupplierReportSearchStore(({ fetching }) => fetching)
  const error = useSupplierReportSearchStore(({ error }) => error)
  const loadMore = useSupplierReportSearchStore(useShallow(({ loadMore }) => loadMore))
  const clearStore = useSupplierReportSearchStore(({ clearStore }) => clearStore)

  const { hasPermissions } = usePermissionManager()
  const hasPurchaseOrderCreationPermission = hasPermissions([PermissionActionType.PURCHASE_ORDER_CREATION])

  const { expanded, table, productRowSelection, getFlexibleColumnWidth, handleProductSelectionChange } =
    useSupplierReportTable()
  const {
    fetching: purchaseOrderFetching,
    isPurchaseOrderDialogOpen,
    selectedCompany,
    selectedCompanyProducts,
    handleStartPO,
    handleCloseDialog,
    handleSubmitPO,
  } = useSupplierReportPurchaseOrder({ data, productRowSelection })

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [throttledLoadMore] = useThrottle(() => loadMore(queryClient, currentSearch), 200)
  const onScroll = useScrollLoadMore({ fetching, loadMore: throttledLoadMore, scrollContainerRef: tableContainerRef })

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clearStore) {
        clearStore()
      }
    }
  }, [clearStore])

  return (
    <>
      {isPurchaseOrderDialogOpen && (
        <PurchaseOrderDialog
          isSubmitting={purchaseOrderFetching}
          company={selectedCompany!} // this is defined when dialog is opened
          products={selectedCompanyProducts}
          onClose={handleCloseDialog}
          onSubmit={handleSubmitPO}
        />
      )}

      <Box className='flex h-full flex-col overflow-hidden rounded text-sm leading-[1.125rem] text-gray-700'>
        {/* Sticky Container */}
        <Box className='sticky z-10'>
          {fetching && data.length !== 0 && (
            <Box className='sticky top-0 z-[60]'>
              <LinearProgress />
            </Box>
          )}
          {/* Headers */}
          <Box className='bg-gray-100 text-gray-700'>
            <Box
              className='grid border-b border-gray-300 tracking-[0.013rem]'
              style={{
                gridTemplateColumns: table.getFlatHeaders().map(getFlexibleColumnWidth).join(" "),
              }}
            >
              {table.getFlatHeaders().map((header) => (
                <Box
                  key={header.id}
                  className={`flex items-center p-4 text-gray-700 ${
                    header.column.getCanSort() ? "cursor-pointer select-none" : ""
                  }`}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{ width: header.getSize() }}
                >
                  <Box className='flex items-center'>
                    {header.column.getCanSort() && (
                      <Box className='mr-2'>
                        {header.column.getIsSorted() === "asc" ? (
                          <ArrowUpwardOutlined fontSize='small' />
                        ) : header.column.getIsSorted() === "desc" ? (
                          <ArrowDownwardOutlined fontSize='small' />
                        ) : (
                          <span className='size-4' /> // Placeholder for unsorted state
                        )}
                      </Box>
                    )}
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box className='grow overflow-auto' onScroll={onScroll} ref={tableContainerRef}>
          <Box className='w-full'>
            {fetching && !data.length ? (
              <Box
                className='grid items-center justify-center bg-white tracking-[0.011rem] text-gray-700'
                style={{
                  gridTemplateColumns: table.getFlatHeaders().map(getFlexibleColumnWidth).join(" "),
                }}
              >
                <Box className='col-span-full flex justify-center p-4'>
                  <CircularProgress />
                </Box>
              </Box>
            ) : (
              !data.length &&
              !fetching && (
                <Box
                  className='grid items-center border-b border-gray-300 bg-white tracking-[0.011rem] text-gray-700'
                  style={{
                    gridTemplateColumns: table.getFlatHeaders().map(getFlexibleColumnWidth).join(" "),
                  }}
                >
                  <Box className='col-span-full px-2'>
                    <Typography variant='body2' className='truncate text-center'>
                      No results found according to your search criteria
                    </Typography>
                  </Box>
                </Box>
              )
            )}
            {table.getRowModel().rows.map((row) => {
              const isExpanded = Boolean(expanded[row.id])
              const companySelectedProducts = Object.values(productRowSelection[row.original.companyId] || {})
              const companySelectedItemsCount = companySelectedProducts.length
              const hasInvalidShipFrom =
                companySelectedProducts.length > 0 && !hasValidShipFromLocations(companySelectedProducts)

              return (
                <SupplierReportProductTableRow
                  key={row.original.companyId}
                  row={row}
                  isExpanded={isExpanded}
                  getFlexibleColumnWidth={getFlexibleColumnWidth}
                >
                  <Box className='w-full'>
                    <Box className='flex items-center'>
                      <Box className='flex w-full flex-col'>
                        <Box className='mb-6 flex justify-between'>
                          <Box>
                            <Typography variant='subtitle1' className='text-gray-700'>
                              {row.original.name}
                            </Typography>
                            <Typography variant='body2' className='text-gray-600'>
                              Vendor no: {row.original.externalSystemId || "-"} |{" "}
                              {formatLocationAddress(
                                row.original?.locationsAssociations?.[0]?.shipping,
                                AddressFormat.Medium
                              )}
                            </Typography>
                          </Box>
                          <Box className='flex items-center gap-4'>
                            <div>
                              <span className='font-bold'>{companySelectedItemsCount} Items</span> selected
                            </div>
                            {hasPurchaseOrderCreationPermission && (
                              <Button
                                endIcon={<ChevronRightOutlined />}
                                disabled={companySelectedItemsCount === 0 || hasInvalidShipFrom}
                                onClick={() => handleStartPO(row.original.companyId)}
                              >
                                Start PO
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box key={`expanded-content-${row.original.companyId}`}>
                      <Suspense fallback={<LinearProgress />}>
                        <LazyExpandedContent
                          companyId={row.original.companyId}
                          rowSelection={Object.values(productRowSelection[row.original.companyId] || {})}
                          onRowSelectionChange={(products) =>
                            handleProductSelectionChange(row.original.companyId, products)
                          }
                        />
                      </Suspense>
                    </Box>
                  </Box>
                </SupplierReportProductTableRow>
              )
            })}
          </Box>
        </Box>

        {/* Error and No Data Messages */}
        {error && (
          <Typography color='error' className='p-4'>
            {error.message}
          </Typography>
        )}
      </Box>
    </>
  )
}
